import { createAsyncThunk, createSlice, PayloadAction, } from '@reduxjs/toolkit'
import { AppState } from '@app/store/state/app.state';
import { Moment } from 'moment';
import { OfficeEntity } from "@features/desks/services/AdvanceHotDeskingService";
import { requestUsersWeeklyMovements, updateWhereaboutsDay } from '@features/whereabouts/services/Movements';
import { loadUsersInOffice, selectOffice } from "./officeView.duck";
import { refreshAllMovements } from "./companyMovements.duck";
import { emptyOfficeUsage, getOfficeCapacities, OfficeCapacityRequest, OfficeUsage } from "@features/desks/services/OfficeCapacityService";
import { failureNotification } from "@shared/store/notification.duck";
import { emptyWhereaboutsDay, Period } from '@features/whereabouts/models/movements.models';
import { DATE_FORMAT } from "@shared/utils/DateUtils";

export interface OfficeUsageState {
  subscriptionsList: OfficeUsage[];
  isLoading: boolean;
}

export const initialOfficeUsageState: OfficeUsageState = {
  subscriptionsList: [],
  isLoading: false,
}

export const subscribeToOfficeUsage: any = createAsyncThunk(
  'officeUsage/subscribeToOfficeUsage',
  async (params: {officeId: number, date: Moment}, thunkAPI) => {
    const officeUsage = (thunkAPI.getState() as AppState).officeUsage;
    const dateString = params.date.format(DATE_FORMAT);
    const officeSub = officeUsage.subscriptionsList.find((ou: OfficeUsage) => ou.officeId === params.officeId && ou.date === dateString);


    if (!officeSub) {
      await thunkAPI.dispatch(addOfficeUsage(emptyOfficeUsage(params.officeId, dateString)));
    }
  }
)

// The server's per-request limits for the batch capacity endpoint.
const BATCH_SIZE = 100;
const BATCH_DATES = 14;

const usageKey = (officeId: number, date: string) => `${officeId}|${date}`;

/**
 * Splits pending usages into groups the batch endpoint accepts: at most BATCH_SIZE items and
 * BATCH_DATES distinct dates each. A page normally needs one group.
 */
export function chunkForBatchRequests(pending: OfficeUsage[]): OfficeUsage[][] {
  const chunks: OfficeUsage[][] = [];
  let current: OfficeUsage[] = [];
  let currentDates = new Set<string>();
  for (const ou of pending) {
    const addsDate = !currentDates.has(ou.date);
    if (current.length >= BATCH_SIZE || (addsDate && currentDates.size >= BATCH_DATES)) {
      chunks.push(current);
      current = [];
      currentDates = new Set<string>();
    }
    current.push(ou);
    currentDates.add(ou.date);
  }
  if (current.length > 0) {
    chunks.push(current);
  }
  return chunks;
}

/**
 * Loads every subscribed office usage that has not loaded yet with one batch request, instead of
 * one request per office and date. Entries are marked loading first so a second call made while
 * this one is in flight does not request them again.
 *
 * Deliberately does not touch `isLoading`: that flag drives the full-screen "saving" spinner and
 * the floor map's reload, and belongs to bookings only.
 */
export const loadUsages: any = createAsyncThunk(
  'officeUsage/loadUsages',
  async (_: void, thunkAPI) => {
    const pending = (thunkAPI.getState() as AppState).officeUsage.subscriptionsList
      .filter(ou => !ou.hasLoaded && !ou.loading);
    if (pending.length === 0) {
      return;
    }

    const requests: OfficeCapacityRequest[] = pending.map(ou => ({officeId: ou.officeId, date: ou.date}));
    thunkAPI.dispatch(markOfficeUsagesLoading(requests));

    const chunks = chunkForBatchRequests(pending);

    await Promise.all(chunks.map(async (chunk) => {
      const failed = (ou: OfficeUsage): OfficeUsage => ({...ou, hasLoaded: true, loading: false, failed: true});
      try {
        const capacities = await getOfficeCapacities(chunk.map(ou => ({officeId: ou.officeId, date: ou.date})));
        thunkAPI.dispatch(replaceOfficeUsages(chunk.map((ou, i) => {
          const capacity = capacities?.[i];
          return capacity && capacity.officeId === ou.officeId
            ? {...ou, ...capacity, date: ou.date, hasLoaded: true, loading: false, failed: false}
            : failed(ou);
        })));
      } catch (e: any) {
        thunkAPI.dispatch(replaceOfficeUsages(chunk.map(failed)));
      }
    }));
  }
)

export const bookSingleDayMovementByOfficeId: any = createAsyncThunk(
  'officeUsage/bookSingleDayMovementByOfficeId',
  async (params: {officeId?: number, period: Period, date: Moment}, thunkAPI) => {
    const appState = thunkAPI.getState() as AppState;
    const offices = appState.advanceHotDeskingSetup.offices;
    const officeEntity = offices.find((o: OfficeEntity) => o.id === params.officeId);
    if (officeEntity) {
      await thunkAPI.dispatch(bookSingleDayMovement({office: officeEntity, period: params.period, date: params.date, userId: appState.auth.currentUser?.id}));
    } else {
      throw new Error('Could not find office: ' + params.officeId);
    }
  }
)

export const bookSingleDayMovement: any = createAsyncThunk(
  'officeUsage/bookSingleDayMovement',
  async (params: {office?: OfficeEntity, period: Period, date: Moment, userId: string}, thunkAPI) => {
    const weekCommencing = params.date.clone().isoWeekday(1);
    let usersWeeklyWhereaboutsResponse = await requestUsersWeeklyMovements(weekCommencing, params.userId);
    let usersWeeklyWhereabouts = usersWeeklyWhereaboutsResponse.whereabouts;
    const appState = thunkAPI.getState() as AppState;

    let amLocationId = 0, pmLocationId = 0;
    let amDeskLocationId, pmDeskLocationId;

    if (params.period === Period.AM) {
      amLocationId = params.office?.id ?? 0;
      amDeskLocationId = 0;
    } else if (params.period === Period.PM) {
      pmLocationId = params.office?.id ?? 0;
      pmDeskLocationId = 0;
    } else if (params.period === Period.AllDay) {
      amLocationId = params.office?.id ?? 0;
      pmLocationId = params.office?.id ?? 0;
      amDeskLocationId = 0;
      pmDeskLocationId = 0;
    }

    const currentUser = (thunkAPI.getState() as AppState).auth.currentUser;
    const carParkingEnabled = !!currentUser?.carParkingEnabled;
    const officeOptionId = appState.whereaboutsOptions.whereaboutsOptions.find(o => o.key === 'OFFICE')?.id;

    const dayToUpdate = usersWeeklyWhereabouts.find(day => day.date === params.date.format(DATE_FORMAT)) || emptyWhereaboutsDay(params.date.format(DATE_FORMAT), params.userId);

    if (amLocationId > 0 && officeOptionId) dayToUpdate.amStatusId = officeOptionId;
    if (pmLocationId > 0 && officeOptionId) dayToUpdate.pmStatusId = officeOptionId;
    dayToUpdate.amOfficeId = amLocationId;
    dayToUpdate.pmOfficeId = pmLocationId;
    dayToUpdate.amDeskId = amDeskLocationId ?? dayToUpdate.amDeskId;
    dayToUpdate.pmDeskId = pmDeskLocationId ?? dayToUpdate.pmDeskId;
    dayToUpdate.amGeneralParking = amLocationId > 0 ? carParkingEnabled : false;
    dayToUpdate.pmGeneralParking = pmLocationId > 0 ? carParkingEnabled : false;

    try {
      await updateWhereaboutsDay(dayToUpdate);
      await new Promise((resolve: any) => setTimeout(() => resolve(), 200));

      // The booking changes usage for that day, possibly in two offices (old and new), so refetch
      // every subscribed count for the date. The loader picks them up in one batch.
      thunkAPI.dispatch(invalidateOfficeUsagesOnDate(params.date.format(DATE_FORMAT)));

      // Get floor if office is an area
      if (params.office?.area && params.office?.parentId) {
        const latestState = thunkAPI.getState() as AppState;
        const offices = latestState.advanceHotDeskingSetup.offices;
        const parentOffice = offices.find((o: OfficeEntity) => o.id === params.office?.parentId)
        if (parentOffice) {
          await thunkAPI.dispatch(selectOffice(parentOffice ?? params.office));
          await thunkAPI.dispatch(loadUsersInOffice({date: params.date, office: parentOffice}));
        }
      } else {
        await thunkAPI.dispatch(selectOffice(params.office));
      }

      await new Promise((resolve: any) => setTimeout(() => resolve(), 100));
      await thunkAPI.dispatch(refreshAllMovements());
      await new Promise((resolve: any) => setTimeout(() => resolve(), 50));
    } catch (e: any) {
      thunkAPI.dispatch(failureNotification(e.message));
    }
  }
)

const officeUsageSlice = createSlice({
  name: 'officeUsage',
  initialState: initialOfficeUsageState,
  reducers: {
    resetOfficeUsage: () => ({...initialOfficeUsageState}),
    addOfficeUsage: (state, action: PayloadAction<OfficeUsage>) => {
      const updatedOfficeUsage = [
        ...state.subscriptionsList,
        // ...state.subscriptionsList.filter((ou: OfficeUsage) => action.payload.officeId !== ou.officeId && action.payload.date !== ou.date),
        action.payload,
      ]
      return {
        ...state,
        subscriptionsList: updatedOfficeUsage
      }
    },
    markOfficeUsagesLoading: (state, action: PayloadAction<OfficeCapacityRequest[]>) => {
      const keys = new Set(action.payload.map(r => usageKey(r.officeId, r.date)));
      return {
        ...state,
        subscriptionsList: state.subscriptionsList.map((ou: OfficeUsage) =>
          keys.has(usageKey(ou.officeId, ou.date)) ? {...ou, loading: true} : ou),
      }
    },
    replaceOfficeUsages: (state, action: PayloadAction<OfficeUsage[]>) => {
      const updates = new Map(action.payload.map(ou => [usageKey(ou.officeId, ou.date), ou]));
      return {
        ...state,
        subscriptionsList: state.subscriptionsList.map((ou: OfficeUsage) => updates.get(usageKey(ou.officeId, ou.date)) ?? ou),
      }
    },
    // Keeps the last known numbers on screen while the refetch is in flight.
    invalidateOfficeUsagesOnDate: (state, action: PayloadAction<string>) => ({
      ...state,
      subscriptionsList: state.subscriptionsList.map((ou: OfficeUsage) =>
        ou.date === action.payload ? {...ou, hasLoaded: false, loading: false, failed: false} : ou),
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(bookSingleDayMovement.pending, (state) => ({...state, isLoading: true}))
      .addCase(bookSingleDayMovement.rejected, (state) => ({...state, isLoading: false}))
      .addCase(bookSingleDayMovement.fulfilled, (state) => ({...state, isLoading: false}));
  }
})

export default officeUsageSlice.reducer;
const { addOfficeUsage, markOfficeUsagesLoading, replaceOfficeUsages } = officeUsageSlice.actions;
export const { invalidateOfficeUsagesOnDate } = officeUsageSlice.actions;

// Selectors
export const selectIsOfficeUsageLoading = (state: AppState) => state.officeUsage.isLoading;
export const selectAllOfficeUsages = (state: AppState) => state.officeUsage.subscriptionsList;
export const selectOfficeCapacityByDateAndOfficeId = (state: AppState, date?: string, officeId?: number) => {
  if (date && officeId) {
    return state.officeUsage.subscriptionsList.find(ou => ou.officeId === officeId && date === ou.date);
  }
}
